import React from "react";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import format from "date-fns/format";
import { useFormikContext } from "formik";
import { SessionDetailsItem } from "@app.automotus.io/components/SessionDetailsItem";
import { formatCurrency } from "common/format";

export interface ViolationPenaltyFormValues {
  // Mirrors the field of the same name owned by EvidenceCaptureValues; kept as an
  // independent declaration here rather than importing across files for this one field --
  // same convention already used by PrefilledDetailsValues.
  violationDateTime: Date | null;
}

export type ViolationPenaltyStatus = "unpaid" | "paid" | "void";

// Named "...Details" rather than reusing the component's own name (ViolationPenalty), which
// would otherwise collide with it in this same file -- TS allows a type and a value to share
// an identifier, but importing both under one name is confusing, not just theoretically risky.
export interface ViolationPenaltyDetails {
  status: ViolationPenaltyStatus;
  amountCents: number;
}

export interface ViolationPenaltyProps {
  /**
   * The computed penalty for the selected violation type -- depends on NoticeDetails's
   * violation-code selection and the violation-types endpoint behind it, see AUT-5574. Omitted
   * in the real app today: this section then renders an honest "-" instead of a fake total.
   */
  penalty?: ViolationPenaltyDetails;
}

const STATUS_LABEL: Record<ViolationPenaltyStatus, string> = {
  unpaid: "Unpaid",
  paid: "Paid",
  void: "Void",
};

const STATUS_COLOR: Record<ViolationPenaltyStatus, "error" | "success" | "text.secondary"> = {
  unpaid: "error",
  paid: "success",
  void: "text.secondary",
};

export const ViolationPenalty: React.FC<ViolationPenaltyProps> = ({ penalty }) => {
  const { values } = useFormikContext<ViolationPenaltyFormValues>();

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" gutterBottom>
          Violation Penalty
        </Typography>
        {penalty && (
          <Typography variant="body2" color={STATUS_COLOR[penalty.status]} fontWeight={600}>
            {STATUS_LABEL[penalty.status]}
          </Typography>
        )}
      </Stack>
      {/* SessionDetailsItem renders itself as a Grid item -- needs a Grid container ancestor
          for its xs/sm sizing to resolve, matching every other consumer in this codebase
          (see PrefilledDetails.tsx, CitationDetails.tsx). */}
      {!penalty ? (
        <Grid container>
          <SessionDetailsItem title="Total" content="-" />
        </Grid>
      ) : (
        <>
          {/* SessionDetailsItem's title is a plain block Typography (unlike its content, which
              sits in a flex box -- see the contentChildren comment below), so `textAlign`
              inherited from an ancestor does move it. Scoped to :last-of-type so only the
              "Amount" item's title flips right, not "Violation Time"'s. */}
          <Grid
            container
            justifyContent="space-between"
            sx={{ "& > .MuiGrid-item:last-of-type": { textAlign: "right" } }}
          >
            <SessionDetailsItem
              title="Violation Time"
              content={values.violationDateTime ? format(values.violationDateTime, "h:mm a") : "-"}
            />
            <SessionDetailsItem
              title="Amount"
              // SessionDetailsItem's default content rendering sits in a `display: flex,
              // justifyContent: flex-start` box, so a `textAlign` override on an ancestor
              // wouldn't move it (that only governs inline text position, not flex-child
              // position). `contentChildren` replaces that default rendering outright, so a
              // full-width wrapper here can align the value itself via its own `textAlign`.
              contentChildren={
                <Typography variant="body1" sx={{ width: "100%", textAlign: "right" }}>
                  ${formatCurrency(penalty.amountCents)}
                </Typography>
              }
            />
          </Grid>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={600}>Total Amount</Typography>
            <Typography fontWeight={600}>${formatCurrency(penalty.amountCents)}</Typography>
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default ViolationPenalty;
